/**
 * `<ThemeToggle />` — Story 5.2.
 *
 * Single button that flips between dark and light via `useTheme()`. Story 5.2
 * ships ONLY this one component in `web/src/components/`; Story 5.4 builds
 * out the rest of the library and owns the directory's organization (no
 * preemptive `index.ts` barrel here per Critical Scope Decision #15).
 *
 * Renders:
 *   - `<button type="button">` with `aria-pressed={theme === 'light'}`,
 *     `aria-label="Switch to <next> theme"`, and a 44×44 minimum tap target
 *     (UX §5.1 line 992).
 *   - Inline SVG sun glyph in dark mode (clicking switches to light); inline
 *     SVG moon glyph in light mode (clicking switches to dark). Inline SVG
 *     is dependency-free per UX §2.4 line 156 ("system font stack — no
 *     web-font load") and theme-aware via `var(--text-primary)` stroke.
 *   - 2px focus-ring outline using `var(--brand-primary)` per UX §5.1 line
 *     985. NOTE: `--brand-primary` is undefined in Story 5.1 and resolves
 *     only when Story 5.6's BrandingProvider mounts; until then the outline
 *     falls back to whatever the browser's default focus indicator renders
 *     (in modern Chromium / Firefox / Safari that's a visible ring).
 *
 * `aria-pressed` semantics (Critical Scope Decision in story spec): we treat
 * dark as the default state, so `aria-pressed="false"` when dark and
 * `aria-pressed="true"` when light. Screen reader announces "Switch to
 * <next> theme" + the toggle's pressed state.
 *
 * Story 7.12 — adds OPTIONAL `onThemeChange` callback prop (CSD #6). The
 * prop fires AFTER the local Story 5.2 write-through (the click handler's
 * synchronous `toggleTheme()` already updated `<html data-theme>`,
 * localStorage, and the cookie before the callback runs). Voter / submitter
 * / broadcast surfaces consume `<ThemeToggle />` WITHOUT the prop and
 * remain unchanged (no remote sync). The admin shell passes a
 * `persistAdminTheme` callback that fires `PATCH /admin/me` to write the
 * Cognito `custom:theme` attribute. Callback failures do NOT block the
 * local toggle (the local write-through is the user-facing affordance).
 */
import { useTheme } from '../theme/useTheme';

const ICON_SIZE_PX = 24;
const TAP_TARGET_MIN_PX = 44;

function SunIcon() {
  // 8-ray sun: center circle + 8 rays (cardinal + diagonal). Stroke uses
  // currentColor so the button can override via `color: var(--text-primary)`.
  return (
    <svg
      width={ICON_SIZE_PX}
      height={ICON_SIZE_PX}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      data-testid="theme-toggle-sun"
    >
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="2" x2="12" y2="4" />
      <line x1="12" y1="20" x2="12" y2="22" />
      <line x1="2" y1="12" x2="4" y2="12" />
      <line x1="20" y1="12" x2="22" y2="12" />
      <line x1="4.93" y1="4.93" x2="6.34" y2="6.34" />
      <line x1="17.66" y1="17.66" x2="19.07" y2="19.07" />
      <line x1="4.93" y1="19.07" x2="6.34" y2="17.66" />
      <line x1="17.66" y1="6.34" x2="19.07" y2="4.93" />
    </svg>
  );
}

function MoonIcon() {
  // Crescent moon path — single closed curve. Same currentColor semantics.
  return (
    <svg
      width={ICON_SIZE_PX}
      height={ICON_SIZE_PX}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      data-testid="theme-toggle-moon"
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

export interface ThemeToggleProps {
  /**
   * Story 7.12 — optional callback invoked AFTER the local theme flip.
   * Receives the theme the toggle just switched TO (`'dark'` or `'light'`).
   *
   * Voter / submitter / broadcast surfaces omit this prop (no remote sync).
   * The admin shell (`<AdminShell>`) passes a `persistAdminTheme` callback
   * that fires `PATCH /admin/me` to write the Cognito `custom:theme`
   * attribute. Callback failures must NOT block the local toggle — the
   * local Story 5.2 write-through has already happened synchronously.
   */
  onThemeChange?: (next: 'dark' | 'light') => void;
}

export function ThemeToggle({ onThemeChange }: ThemeToggleProps = {}) {
  const { theme, toggleTheme } = useTheme();
  const nextTheme = theme === 'dark' ? 'light' : 'dark';

  // Local Story 5.2 write-through happens synchronously inside `toggleTheme`.
  // The callback fires AFTER, so even if it throws / rejects, the user's
  // CURRENT-device theme experience is intact. CSD #6 sub-lock — local
  // store is the user's CURRENT-device state regardless of remote sync.
  const handleClick = () => {
    toggleTheme();
    if (onThemeChange) {
      try {
        onThemeChange(nextTheme);
      } catch {
        // Swallow synchronous throws so the toggle UI is never blocked by
        // a misbehaving consumer. Rejected promises are the consumer's
        // responsibility (the AdminShell wrapper logs + toasts on failure).
      }
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-pressed={theme === 'light'}
      aria-label={`Switch to ${nextTheme} theme`}
      style={{
        // 44x44 minimum tap target (UX §5.1 line 992). Inline-style values
        // are read by the JSDOM test (computed layout is unavailable).
        minWidth: TAP_TARGET_MIN_PX,
        minHeight: TAP_TARGET_MIN_PX,
        // Token-based styling consumes Story 5.1's tokens directly. NO
        // hard-coded hex.
        background: 'var(--bg-surface)',
        color: 'var(--text-primary)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        // 2px focus ring. `--brand-primary` is undefined until Story 5.6;
        // until then the browser default focus indicator wins.
        // Story 5.4 review L1 carry-forward (Critical Scope Decision #6):
        // explicit outlineWidth + outlineStyle complete the contract that
        // outlineColor + outlineOffset alone would leave dormant.
        outlineWidth: 2,
        outlineStyle: 'solid',
        outlineColor: 'var(--brand-primary)',
        outlineOffset: 2,
      }}
    >
      {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
    </button>
  );
}
